/**
 * What Maria can sell this customer, and at what price (MAR-219, MAR-133).
 *
 * The mobile version of this reads prices out of StoreKit, where a product
 * that failed to load simply is not in the list. The web version reads them
 * out of `GET /api/mum-hub/web-config`, and the rule that survives the port is
 * the important one: A PRODUCT WITH NO PRICE IS NOT AN OFFER. It is dropped,
 * not shown at a guess and not shown with the price left blank, because a row
 * that says "Unlock the whole journey" with nothing beside it reads as free.
 *
 * ## The ladder is the Worker's, and now so is everything on it
 *
 * `entitlements.offerCatalogue` arrives in display order carrying, per row, the
 * catalogue key `/checkout` takes, whether it sells one stage or a pack, EVERY
 * stage it grants and how many months it would give her if she bought it today.
 * This module orders and prices that list and never adds to it.
 *
 * That is what makes Adrian's rule true here without this file knowing it:
 * "don't show the 1st trimester if the person is in the second one, the same
 * for the others". A stage behind her is not in the catalogue, so it cannot be
 * drawn, and neither can a pack that would make her pay twice for a stage she
 * owns. Working that out on the client is how a website and an app come to
 * disagree about what a customer may buy.
 *
 * ## The saving is arithmetic or it is nothing
 *
 * "Save £5.99" is a claim about two real prices, so it is computed from them:
 * the pack's own stages added up, less the pack price, and only from the very
 * rows drawn beside it. If any one of them is missing there is no saving to
 * state and the line is omitted. It is never a number typed in, and it is never
 * shown when it comes out zero or negative, which would be a pricing mistake
 * advertised as a discount.
 */

import {
  MUM_HUB_JOURNEY_KEY,
  MUM_HUB_JOURNEY_UPGRADE_KEY,
  MUM_HUB_STAGES,
  stageForProductKey,
  type MumHubStageDescriptor,
  type MumHubStageId,
} from "./stages";
import type { MumHubPrice, MumHubWebConfig } from "./api";
import { STAGE_ACCESS_MONTHS, journeyAccessMonths, upgradeAccessMonths } from "./access";

/**
 * What kind of thing a row sells.
 *
 * Two shapes, not three. The whole journey is a PACK of four stages rather than
 * a kind of its own: to a paywall it is a set of stages bought once, which is
 * exactly what the two- and three-stage packs are with fewer stages in them.
 * Keeping "journey" as a kind is what made a third of this file a special case,
 * and it could not have described a two-stage pack at all.
 */
export type MumHubOfferKind = "stage" | "pack";

/**
 * One thing she may be offered, as the Worker describes it.
 *
 * The wire shape of `offerCatalogue`, with one deliberate widening: `months` is
 * nullable here and is not on the wire. The Worker always knows the number; the
 * fallback below sometimes cannot, and null is drawn as "up to twelve months"
 * rather than as a term of sale invented by a browser.
 */
export interface MumHubOfferEntry {
  /** The catalogue key. What `/checkout` takes and what `web-config` prices. */
  key: string;
  /** The App Store product id, carried for parity. Unused on the website. */
  productId: string;
  /** The Stripe Price lookup key. Adrian's handle on the price in Stripe. */
  stripeLookupKey: string;
  kind: MumHubOfferKind;
  /** Every stage it grants, in the order a pregnancy runs. */
  stageSlugs: string[];
  /** Months of access it would give her if she bought it today, or null. */
  months: number | null;
}

const STAGE_SLUGS: readonly string[] = MUM_HUB_STAGES.map((stage) => stage.slug);

function strings(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter(
        (value): value is string => typeof value === "string" && value.length > 0,
      )
    : [];
}

/**
 * One catalogue entry off the wire, or null.
 *
 * Strict about the two fields a row cannot be drawn without. A row with no key
 * cannot be priced or checked out, and a row whose `kind` this build does not
 * recognise would have to be guessed into one of two very different sentences.
 * Both are dropped, which costs her one offer she can still reach another way,
 * rather than shown wrong.
 *
 * `months` is read leniently on purpose: a missing or absurd duration becomes
 * null, and null has an honest rendering. Refusing the whole row over it would
 * hide a product she can buy because of a label.
 */
export function readOfferEntry(raw: unknown): MumHubOfferEntry | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const key = typeof row.key === "string" ? row.key : "";
  if (!key) return null;
  if (row.kind !== "stage" && row.kind !== "pack") return null;
  const months =
    typeof row.months === "number" && Number.isFinite(row.months) && row.months > 0
      ? row.months
      : null;
  return {
    key,
    productId: typeof row.productId === "string" ? row.productId : "",
    stripeLookupKey:
      typeof row.stripeLookupKey === "string" ? row.stripeLookupKey : "",
    kind: row.kind,
    stageSlugs: strings(row.stageSlugs).filter((slug) => STAGE_SLUGS.includes(slug)),
    months,
  };
}

/** The whole `offerCatalogue` array off the wire. Absent reads as empty. */
export function readOfferCatalogue(raw: unknown): MumHubOfferEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(readOfferEntry)
    .filter((entry): entry is MumHubOfferEntry => entry !== null);
}

const STAGE_BY_KEY = new Map<string, MumHubStageDescriptor>(
  MUM_HUB_STAGES.map((stage) => [stage.productKey, stage]),
);

/**
 * The catalogue an OLD Worker would have implied, from `offerKeys` alone.
 *
 * The thin compatibility layer and nothing more. A Worker that predates
 * MAR-133 sends the two lists of identifiers and no catalogue, and the site
 * must keep selling exactly what it sold yesterday rather than showing
 * "Nothing to buy here" until the next deploy.
 *
 * The mapping is the one that was hardcoded in this file until 2026-09-13: a
 * stage key grants its stage for four months, the journey grants all four
 * stages for as long as her date allows, and the retired upgrade grants the
 * stages she does not own for the journey's allowance less the time already
 * spent. A key this build has never seen is dropped: a row with no stages and
 * no duration is not an offer.
 */
export function fallbackOfferCatalogue({
  offerKeys,
  ownedStageIds: owned = [],
  dueDate = null,
  stagePurchasedAt = null,
  now = new Date(),
}: {
  offerKeys: readonly string[];
  ownedStageIds?: readonly MumHubStageId[];
  dueDate?: string | null;
  stagePurchasedAt?: string | null;
  now?: Date;
}): MumHubOfferEntry[] {
  const journeyMonths = journeyAccessMonths(dueDate, now);
  const entries: MumHubOfferEntry[] = [];
  for (const key of offerKeys) {
    const stage = STAGE_BY_KEY.get(key);
    if (stage) {
      entries.push({
        key,
        productId: "",
        stripeLookupKey: "",
        kind: "stage",
        stageSlugs: [stage.slug],
        months: STAGE_ACCESS_MONTHS,
      });
      continue;
    }
    if (key === MUM_HUB_JOURNEY_KEY) {
      entries.push({
        key,
        productId: "",
        stripeLookupKey: "",
        kind: "pack",
        stageSlugs: [...STAGE_SLUGS],
        months: journeyMonths,
      });
      continue;
    }
    if (key === MUM_HUB_JOURNEY_UPGRADE_KEY) {
      entries.push({
        key,
        productId: "",
        stripeLookupKey: "",
        kind: "pack",
        stageSlugs: MUM_HUB_STAGES.filter(
          (stage) => !owned.includes(stage.id),
        ).map((stage) => stage.slug),
        months: upgradeAccessMonths({ journeyMonths, stagePurchasedAt, now }),
      });
    }
  }
  return entries;
}

/**
 * The catalogue to draw: the Worker's when it sent one, the derived one when it
 * did not.
 *
 * An empty `offerCatalogue` is NOT the same as an absent one and is not routed
 * through the fallback. She owns everything, or nothing is on sale, and both of
 * those are "no rows". Falling back there would resurrect a ladder the Worker
 * had deliberately emptied.
 */
export function mumHubOfferEntries({
  offerCatalogue,
  hasCatalogue,
  offerKeys,
  ownedStageIds: owned = [],
  dueDate = null,
  stagePurchasedAt = null,
  now = new Date(),
}: {
  offerCatalogue: readonly MumHubOfferEntry[];
  /** Whether the payload carried `offerCatalogue` at all. */
  hasCatalogue: boolean;
  offerKeys: readonly string[];
  ownedStageIds?: readonly MumHubStageId[];
  dueDate?: string | null;
  stagePurchasedAt?: string | null;
  now?: Date;
}): MumHubOfferEntry[] {
  if (hasCatalogue) return [...offerCatalogue];
  return fallbackOfferCatalogue({
    offerKeys,
    ownedStageIds: owned,
    dueDate,
    stagePurchasedAt,
    now,
  });
}

/**
 * Pence to a printed price.
 *
 * Whole pounds print without pennies, which is what every price on this site
 * does, unless `alwaysShowPennies` is set.
 *
 * Null rather than a fallback string when the amount or the currency is not
 * usable: the caller's job is then to omit the line, and a "£0.00" here would
 * be printed beside a buy button.
 */
export function formatPence(
  amountPence: number,
  currency: string,
  { alwaysShowPennies = false } = {},
): string | null {
  if (!Number.isFinite(amountPence) || !currency) return null;
  const amount = amountPence / 100;
  const whole = Math.round(amountPence) % 100 === 0;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      minimumFractionDigits: whole && !alwaysShowPennies ? 0 : 2,
      maximumFractionDigits: whole && !alwaysShowPennies ? 0 : 2,
    }).format(amount);
  } catch {
    // An unrecognised currency code throws inside Intl. Better no price than a
    // number with no unit on it.
    return null;
  }
}

export interface MumHubOfferRow {
  /** The catalogue key. This is what the checkout is opened with. */
  productKey: string;
  kind: MumHubOfferKind;
  /** Every stage this row grants, in the order a pregnancy runs. */
  stageSlugs: string[];
  /**
   * Those stages' names, carried on the row so the paywall never has to look a
   * stage up to write its own label, which is the kind of second lookup that
   * ends up using a different table.
   */
  stageLabels: string[];
  /** Whether this is the single stage she is standing in. Drives the ordering. */
  isHerStage: boolean;
  /**
   * True for the pack. It leads the list, it carries the badge and it is
   * preselected, which is Adrian's decision: "make sure that for the whole
   * journey ones we show 'best value' and we select it by default".
   */
  bestValue: boolean;
  price: MumHubPrice;
  displayPrice: string;
  /** "£5.99", or null when it cannot be computed from real prices. */
  saving: string | null;
  /**
   * How many months of access this row buys, or null when the Worker could not
   * say and the fallback could not work it out (MAR-247).
   *
   * Null is drawn as "up to twelve months" and never as nothing, because how
   * long the access lasts is the thing she most needs before she pays.
   */
  accessMonths: number | null;
}

export interface MumHubOffers {
  rows: MumHubOfferRow[];
  /** Which row starts selected, or null when there is nothing to sell. */
  selectedKey: string | null;
}

const STAGE_BY_SLUG = new Map<string, MumHubStageDescriptor>(
  MUM_HUB_STAGES.map((stage) => [stage.slug, stage]),
);

/**
 * Build the paywall's rows.
 *
 * ## Order
 *
 * The pack first, then her own stage, then the later stages in the order a
 * pregnancy runs. The Worker's own order puts her stage first and the pack
 * under it; Adrian asked for the pack to lead, because it carries the badge and
 * the saving and it is the row preselected, and a preselected row that is not
 * the first row reads as a mistake.
 *
 * ## Price
 *
 * A key the catalogue offers but the config does not price is dropped: the
 * backend decides what she may buy, the config decides what it costs, and a row
 * needs both.
 */
export function buildMumHubOffers({
  entries,
  config,
  herStageId,
}: {
  entries: readonly MumHubOfferEntry[];
  config: MumHubWebConfig;
  herStageId: MumHubStageId | null;
}): MumHubOffers {
  const priced = entries.filter((entry) => config.prices[entry.key] !== undefined);

  const herSlug =
    herStageId === null
      ? null
      : (MUM_HUB_STAGES.find((stage) => stage.id === herStageId)?.slug ?? null);

  const orderOf = (entry: MumHubOfferEntry): number => {
    if (entry.kind === "pack") return 0;
    const slug = entry.stageSlugs[0] ?? "";
    if (herSlug && slug === herSlug) return 1;
    const index = STAGE_SLUGS.indexOf(slug);
    return 2 + (index < 0 ? STAGE_SLUGS.length : index);
  };
  const sorted = [...priced].sort((a, b) => orderOf(a) - orderOf(b));

  const rows = sorted.map((entry): MumHubOfferRow => {
    const price = config.prices[entry.key] as MumHubPrice;
    const slug = entry.kind === "stage" ? (entry.stageSlugs[0] ?? null) : null;
    return {
      productKey: entry.key,
      kind: entry.kind,
      stageSlugs: [...entry.stageSlugs],
      stageLabels: entry.stageSlugs
        .map((candidate) => STAGE_BY_SLUG.get(candidate)?.label)
        .filter((label): label is string => label !== undefined),
      isHerStage: !!slug && slug === herSlug,
      bestValue: entry.kind === "pack",
      price,
      // Non-null by construction: `readWebConfig` refuses a price with an
      // unusable amount or currency, so the only way `formatPence` can fail
      // here is an `Intl` throw on a currency code that got past that.
      displayPrice: formatPence(price.amountPence, price.currency) ?? "",
      saving: savingFor(entry, price, config.prices, priced),
      accessMonths: entry.months,
    };
  });

  // A row whose price would not print is not an offer. It is the last place a
  // blank price could reach a button, and it is cheaper to drop it here than
  // to make every caller check.
  const usable = rows.filter((row) => row.displayPrice !== "");

  const selectedKey =
    usable.find((row) => row.kind === "pack")?.productKey ??
    usable.find((row) => row.isHerStage)?.productKey ??
    usable[0]?.productKey ??
    null;

  return { rows: usable, selectedKey };
}

/**
 * What a pack saves against its own stages bought one by one, or null.
 *
 * The stage prices are looked up through the CATALOGUE rather than through the
 * stage table, so a pack is always compared against the very rows drawn beside
 * it. One missing stage price and the whole comparison is dropped: a saving
 * over two of three stages is a smaller number wearing a bigger one's clothes.
 */
function savingFor(
  entry: MumHubOfferEntry,
  price: MumHubPrice,
  prices: MumHubWebConfig["prices"],
  entries: readonly MumHubOfferEntry[],
): string | null {
  if (entry.kind !== "pack" || entry.stageSlugs.length === 0) return null;
  let separately = 0;
  for (const slug of entry.stageSlugs) {
    const single = entries.find(
      (candidate) => candidate.kind === "stage" && candidate.stageSlugs[0] === slug,
    );
    const piece = single ? prices[single.key] : undefined;
    if (!piece || piece.currency !== price.currency) return null;
    separately += piece.amountPence;
  }
  const saved = separately - price.amountPence;
  if (saved <= 0) return null;
  return formatPence(saved, price.currency);
}

/**
 * The stages she already owns, as ids, from the entitlements payload.
 *
 * The payload keys stages by content SLUG and everything on the page reasons
 * in stage IDs, so the translation happens once, here, rather than in each
 * caller. A slug that is not one of the four is dropped rather than guessed
 * at: `all` is the field that means everything, not an unrecognised key.
 */
export function ownedStageIds(
  stages: Record<string, unknown>,
  all: boolean,
): MumHubStageId[] {
  if (all) return MUM_HUB_STAGES.map((stage) => stage.id);
  return MUM_HUB_STAGES.filter((stage) => stages[stage.slug] !== undefined).map(
    (stage) => stage.id,
  );
}

/**
 * What a piece's price line prints.
 *
 * Two strings rather than one, because a sale is drawn and not written: the
 * regular price is struck through and the price she pays is the emphasised
 * one, exactly as the shop's `ProductCard` does it. Joining them into a single
 * string here would force every surface to invent its own markup, and they
 * would drift.
 */
export interface PiecePriceLabels {
  /** What she pays today. Always present. */
  current: string;
  /** The regular price, struck through, or null when this is not a sale. */
  original: string | null;
}

/**
 * A piece's price line, or null when there is no price to print.
 *
 * The sale comparison is NOT made here. `readOriginalPricePence` has already
 * refused anything that is not strictly greater than what she pays, so by the
 * time a piece reaches a screen an `originalPricePence` that is present is a
 * real saving. This function's only judgement is that an original which will
 * not format is the same as no original: better a plain price than a
 * strike-through with nothing beside it.
 */
export function piecePriceLabels(item: {
  pricePence: number | null;
  originalPricePence: number | null;
  currency: string;
}): PiecePriceLabels | null {
  if (item.pricePence === null) return null;
  const current = formatPence(item.pricePence, item.currency);
  if (current === null) return null;
  return {
    current,
    original:
      item.originalPricePence === null
        ? null
        : formatPence(item.originalPricePence, item.currency),
  };
}
